import React, { useEffect, useRef } from 'react'
import { PropsWithChildren } from 'react'
import * as Sentry from '@sentry/react'
import { PylonContext } from './context'
import { PylonContextValues, PylonProviderProps, PylonIdentifyParams } from './types'
import { initialize } from './initialize'
import { PylonApi } from './api'

export const PylonProvider: React.FC<PropsWithChildren<PylonProviderProps>> = ({
  appId,
  onOpen,
  onClose,
  children,
}) => {
  const isRegisteredCb = useRef(false)
  const [isOpen, setIsOpen] = React.useState(false)

  const show = React.useCallback(() => {
    PylonApi('show')
  }, [])

  const hide = React.useCallback(() => {
    PylonApi('hide')
  }, [])

  const open = React.useCallback(() => {
    PylonApi('open')
  }, [])

  const close = React.useCallback(() => {
    PylonApi('close')
  }, [])

  const identify = React.useCallback(
    (params: PylonIdentifyParams) => {
      // Pylon uses a global config object for chat settings; keep both for safety
      const win = window
      win.pylon = {
        ...win.pylon,
        chat_settings: {
          ...(win.pylon?.chat_settings || {}),
          app_id: appId,
          email: params.email,
          name: params.name,
          avatar_url: params.avatarUrl,
          tags: params.tags,
          account_external_id: params.accountExternalId,
        },
      }
      PylonApi('identify', {
        email: params.email,
        name: params.name,
        user_id: params.userId,
        avatar_url: params.avatarUrl,
        tags: params.tags,
        account_external_id: params.accountExternalId,
      })
    },
    [appId],
  )

  const reset = React.useCallback(() => {
    PylonApi('reset')
  }, [])

  useEffect(() => {
    function registerCallback() {
      if (isRegisteredCb.current) {
        return
      }
      // Many widgets support event hooks; keep minimal state handling
      try {
        PylonApi('on', 'open', function () {
          setIsOpen(true)
          onOpen && onOpen()
        })
        PylonApi('on', 'close', function () {
          setIsOpen(false)
          onClose && onClose()
        })
      } catch (_) {
        // no-op if API doesn't support events
      }
      isRegisteredCb.current = true
    }

    initialize(appId, registerCallback, (error) => {
      Sentry.captureException(error, { extra: { message: 'Pylon initialization failed' } })
    })
  }, [appId, onOpen, onClose])

  const initialProviderValue: PylonContextValues = {
    show,
    hide,
    open,
    close,
    identify,
    reset,
    isOpen,
  }

  return <PylonContext.Provider value={initialProviderValue}>{children}</PylonContext.Provider>
}
